import { useHistory } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { CREATE_GRAPHIC_STAND, UPDATE_GRAPHIC_STAND } from 'graphql/mutation/graphic-stand-gql';
import { GET_STANDS_CURSOR } from 'graphql/query/stand-gql';
import { GET_GRAPHIC_STANDS_CURSOR } from 'graphql/query/graphic-stand-gql';
import { successNotification, errorNotification } from 'components/request-result';
import GraphicStandConst from '../graphic-stand-const';
import GraphicPacking from 'components/graphic-packing';

const useGraphicStandMutationCreateUpdate = (options = {}) => {
    const history = useHistory();
    const client = useApolloClient();
    const { graphicStandId, onCompleted, onError, skipNotifications = false } = options;

    const MUTATION = useMemo(() =>
        graphicStandId ? UPDATE_GRAPHIC_STAND : CREATE_GRAPHIC_STAND,
        [graphicStandId]
    );

    const mutationName = useMemo(() =>
        graphicStandId ? 'graphicStandUpdate' : 'graphicStandCreate',
        [graphicStandId]
    );

    const refetchQueries = useMemo(() => [
        {
            query: GET_STANDS_CURSOR,
            variables: {
                exclude_graphics_sent: true,
                perPage: 100,
                whereCrafts: {
                    column: 'TYPE',
                    value: "collect_graphics"
                }
            }
        },
        {
            query: GET_GRAPHIC_STANDS_CURSOR
        }
    ], []);

    const handleCompleted = useCallback((data) => {
        const {
            [ mutationName ]: {
                label,
                message,
                graphicStand
            }
        } = data;

        // Force update table cache
        client.refetchQueries({
            include: [GET_STANDS_CURSOR, GET_GRAPHIC_STANDS_CURSOR]
        });

        if (!options.skipRedirect) {
            history.push(`${ GraphicPacking.Const.basePath }/${ graphicStand.graphic_list_id }${ GraphicStandConst.basePath }`)
        }

        if (!skipNotifications) {
            successNotification({
                title: label,
                description: message
            });
        }

        if (onCompleted) {
            onCompleted(graphicStand);
        }
    }, [mutationName, options.skipRedirect, skipNotifications, onCompleted, history, client]);

    const handleError = useCallback((error) => {
        if (!skipNotifications) {
            errorNotification(error);
        }

        if (onError) {
            onError(error);
        }
    }, [skipNotifications, onError]);

    const [ mutate, { loading } ] = useMutation(MUTATION, {
        refetchQueries,
        onCompleted: handleCompleted,
        onError: handleError
    });


    const create = useCallback((variables) => {
        return mutate(variables);
    }, [mutate]);

    const update = useCallback((variables) => {
        return mutate(variables);
    }, [mutate]);

    const result = useMemo(() => ({
        create,
        update,
        loading
    }), [create, update, loading]);

    return result;
};

export default useGraphicStandMutationCreateUpdate;